import { defineStore } from 'pinia'
import { TrackMode, TrackType } from '@ve-pro/enums/track-type'
import type {
  SegmentCollection,
  SegmentGroupCollection,
  SegmentNoGroupCollection,
  Track,
  SegmentGroups,
  SegmentFragment,
  SegmentVideo,
  SegmentSubtitleGroup,
} from '@ve-pro/types/track.type'
import { getGroupDefaultSegment } from './segment/segment-group'
import { ProjectMode, ProjectTab } from '@ve-pro/enums/project'
import type { SegmentText, InferGroup } from '@ve-pro/types/track.type'
import { isSegmentGroup } from '@ve-pro/store/tracks/segment/segment-group'
import {
  getTracksMaxDuration,
  getDefaultTracks,
  isFragmentTrack,
  groupFragmentTracks,
  isTemplateTrack,
  getLanguageTracks,
  addSegment,
  isLangTrack,
} from './tracks-actions'
import { useProjectStore } from '../project'
import type { NonNullable } from 'types'
import {
  createAndAddDefaultModeMainTrack,
  createTrack,
} from './add-segment-action'
import { createFragmentTrack } from './add-segment-fragment'
import { getSortedSegments } from '@ve-pro/store/tracks/update-main-track'
import { isSegmentFragment } from '@ve-pro/store/tracks/segment/segment-fragment'
import {
  resolveSegmentProperties,
  resolveSegmentSubtitleGroup,
} from './resolve-segment'
import { reactive } from 'vue'
import { SegmentType } from '@ve-pro/enums/segment'
import { isSegmentSubtitleGroup } from './segment/segment-subtitle'
import { _getAllSegments } from '@ve-pro/store/tracks/segment'
import { isLangSegment } from '@ve-pro/store/tracks/tracks-actions'

// 指定剧本获取主轨道
// 剧本类型：
// 1.传统时间轴剧本
// 2.片段时间轴
// 3.片段编辑是传统时间轴剧本
export function getMainTrack(tracks: Track[]) {
  const isFn = (track: Track): track is Track<TrackType.Video> =>
    track.type === TrackType.Video && track.is_main
  return tracks.find(isFn) ?? null
}

export const TRACK_SUBTITLES = 'track_subtitles'

function getTracksByFragmentSegmentId(
  fragment_segment_id: SegmentCollection['fragment_segment_id'],
  tracks: Track[],
): Track[] {
  return tracks.filter((track) => {
    return (
      isFragmentTrack(track) &&
      track.fragment_segment_id === fragment_segment_id
    )
  })
}

export const useTrackStore = defineStore('tracks', {
  state: () => {
    return {
      mode: TrackMode.Tradition as TrackMode,
      // 轨道区域（索引越小，轨道就在越底下，层级越低）
      tracks: [] as Track[],
      // 当前时间(单位毫米)
      currentTime: 0,
      /**
       * 当前选中的元件组/普通元件
       */
      activeSegment: null as SegmentCollection | null,
      copiedSegment: null as SegmentCollection | null,

      /** 当前选中的多个元件id */
      activeSegments: [] as SegmentCollection['id'][],

      // 元件拖拽中....
      dragging: false,
      // 元件裁剪中...
      cutting: false,
      // 是否折叠音轨
      collapseAudioTrack: false,

      // 元件组的子元件数据集合
      segmentGroups: {} as SegmentGroups,

      /**
       * 当前进入的元件组
       */
      activeSegmentGroup: null as SegmentGroupCollection | null,
      /**
       * 当前进入的片段元件
       */
      activeSegmentFragment: null as SegmentFragment | null,
    }
  },
  getters: {
    // 创意制作剧本（所有片段内容剧本 + 非片段内容剧本）
    defaultTracks(state): Track[] {
      const tracks = getDefaultTracks(
        state.tracks.filter((t) => !isTemplateTrack(t)),
      )
      if (this.subtitleTracks.length) {
        const tid = TRACK_SUBTITLES
        const sid = Number.MAX_SAFE_INTEGER
        // 折叠字幕轨道
        const _collapseSubtitlesTrack = createTrack(TrackType.Text)
        _collapseSubtitlesTrack.id = tid

        const _group = resolveSegmentSubtitleGroup()
        _group.id = sid
        _group.stage === ProjectMode.Default

        resolveSegmentProperties(_group)
        _group.start_frame = 0
        // 这个需要在应用的地方，去获取totalTime
        // __group.duration_frames = 0
        addSegment(_group, _collapseSubtitlesTrack)

        tracks.push(reactive(_collapseSubtitlesTrack))
      }

      return getLanguageTracks(tracks)
    },
    // 仅包含“创意制作”，即拆分后不包含片段剧本的剧本
    defaultExcludeFragmentTracks(): Track[] {
      return this.defaultTracks.filter((track) => !isFragmentTrack(track))
    },
    // 编辑中的片段剧本
    currentEditingSegmentFragmentTracks(): Track[] | null {
      const segmentFragment = this.activeSegmentFragment
      if (segmentFragment) {
        return getTracksByFragmentSegmentId(
          segmentFragment.id,
          this.tracks,
        ).filter((t) => !isLangTrack(t))
      }

      return null
    },

    // 字幕&配音剧本
    subtitleTracks(state): Track[] {
      return getLanguageTracks(
        state.tracks.filter((t) =>
          t.segments.some((seg) => seg.stage === ProjectMode.Subtitle),
        ),
      )
    },
    templateTracks(state) {
      return getLanguageTracks(state.tracks.filter(isTemplateTrack))
    },
    selectedTemplate() {
      return this.templateTracks.length > 0
    },

    // 视频的总时长(单位毫秒)
    totalTime(): number {
      return getTracksMaxDuration(
        this.defaultExcludeFragmentTracks.filter(
          (t) => !t.segments.some((s) => isSegmentSubtitleGroup(s)),
        ),
      )
    },

    // 全量剧本的
    segments(): SegmentCollection[] {
      return this.tracks.reduce<SegmentCollection[]>((result, track) => {
        return [...result, ...track.segments]
      }, [])
    },
    // 创意制作 全部字幕的载体
    subtitleGroups(): SegmentSubtitleGroup[] {
      return (
        this.defaultTracks
          .find((t) => t.id === TRACK_SUBTITLES)
          ?.segments.filter(
            (s): s is SegmentSubtitleGroup =>
              s.type === SegmentType.SubtitleGroup,
          ) ?? []
      )
    },

    // 所有的元件数据，包含元件、组元件、子元件、多语言的元件
    allSegments(): SegmentCollection[] {
      return _getAllSegments(this.segments, this.segmentGroups)
    },

    // 所有非多语言的元件
    allUnLangSegments(): SegmentCollection[] {
      return this.allSegments.filter((s) => !isLangSegment(s))
    },

    // 模版主视频
    templateMainVideo(): undefined | SegmentVideo {
      return this.segments.find(
        (t) => t.type === SegmentType.Video && t.is_main_video,
      ) as SegmentVideo
    },
    defaultExcludeFragmentMainTrack(): ReturnType<typeof getMainTrack> {
      return getMainTrack(this.defaultExcludeFragmentTracks)
    },
    // 返回主轨道（若轨道数组不为空，则一定存在）
    mainTack(): ReturnType<typeof getMainTrack> {
      let tracks = this.defaultExcludeFragmentTracks
      // 片段编辑的主轨道，特殊处理
      if (this.isEnterSegmentFragmentEditMode) {
        tracks = this.currentEditingSegmentFragmentTracks ?? []
      }

      return getMainTrack(tracks)
    },

    segmentTexts(state): SegmentText[] {
      const result: InferGroup<SegmentText, false>[] = []

      state.tracks
        .filter((track) => track.type === TrackType.Text)
        .reverse()
        .forEach((track) => {
          const temp = [...track.segments]
          temp
            .sort((a, b) => a.start_time - b.start_time)
            .forEach((segment) => {
              if (isSegmentGroup(segment)) {
                const children = (this.segmentGroups[segment.id] ||
                  []) as typeof result
                result.push(...children)
              } else {
                result.push(segment as typeof result[0])
              }
            })
        })

      return result
    },

    // 当前（默认）选中的子元件
    activeChildSegment(state): SegmentNoGroupCollection | null {
      if (state.activeSegmentGroup) {
        // 资源组为空的时候，返回第一个资源对应的子元件
        // 优先返回失败的元件
        const deprecatedSegment = state.segmentGroups[
          state.activeSegmentGroup.id
        ]?.find((s) => s.deprecated)

        return (
          getGroupDefaultSegment(state.activeSegmentGroup) ??
          deprecatedSegment ??
          null
        )
      }

      return null
    },

    // 是否进入组模式
    isEnterSegmentGroupMode(): boolean {
      const projectStore = useProjectStore()
      if (projectStore.mode === ProjectMode.Default) {
        return Boolean(this.activeSegmentGroup)
      }

      return false
    },
    // 是否进入片段编辑模式
    isEnterSegmentFragmentEditMode(): boolean {
      const projectStore = useProjectStore()
      if (
        projectStore.mode === ProjectMode.Default &&
        this.mode === TrackMode.Fragment
      ) {
        return Boolean(this.activeSegmentFragment)
      }

      return false
    },

    // 是否选中了多个元件
    isSelectMultipleSegments(): boolean {
      return this.activeSegments.length > 1
    },

    defaultFragments(): SegmentFragment[] {
      if (this.defaultExcludeFragmentMainTrack) {
        const segments = getSortedSegments({
          track: this.defaultExcludeFragmentMainTrack,
        }) as any[]

        return segments.filter((fragment): fragment is SegmentFragment =>
          isSegmentFragment(fragment),
        )
      }
      return []
    },

    /**
     * 分组后的分片轨道数据
     */
    fragmentTracks(state) {
      return groupFragmentTracks(state.tracks)
    },

    /**
     * 需要折叠的轨道
     */
    collapseTracks() {
      const result: Track[] = []
      const templateTracksMainTrack = getMainTrack(this.templateTracks)
      if (templateTracksMainTrack) {
        result.push(templateTracksMainTrack)
      }

      return result
    },
  },
  actions: {
    getMainTrack,
    // 若默认的模式轨道，则创建默认的主轨道
    createDefaultModeTrackMainIfNotExist(): NonNullable<
      ReturnType<typeof getMainTrack>
    > {
      const mainTack =
        getMainTrack(this.defaultExcludeFragmentTracks) ??
        createAndAddDefaultModeMainTrack()

      return mainTack
    },

    /**
     * 按所在独立剧本进行判断是否是主轨道
     */
    isMainTrackInBelongTracks(track: Track): boolean {
      const mainTrack = isFragmentTrack(track)
        ? getMainTrack(
            this.getTracksByFragmentSegmentId(track.fragment_segment_id!),
          )
        : this.defaultExcludeFragmentMainTrack
      return mainTrack?.id === track.id
    },

    /**
     * @deprecated
     * @description 已经对使用场景很模糊了：不确定使用场景是要跟进当前环境的剧本做片段，还是按所在独立剧本进行判断
     * @param track
     * @returns
     */
    isMainTrack(track: Track): boolean {
      return track.id === this.mainTack?.id
    },
    // 片段时间轴轨道下（非片段编辑）是不是主轨道
    isFragmentModeMainTrack(track: Track): boolean {
      return (
        this.mode === TrackMode.Fragment &&
        track.id === getMainTrack(this.defaultExcludeFragmentTracks)?.id
      )
    },
    createFragmentMainTrackIfNotExist(
      fragment_segment_id: SegmentCollection['fragment_segment_id'],
    ): Track<TrackType.Video> {
      const tracks = this.getTracksByFragmentSegmentId(fragment_segment_id)
      let mainTack = getMainTrack(tracks)
      // 当不存在主轨道时候，表示轨道数据tracks为空
      if (!mainTack) {
        mainTack = createFragmentTrack(fragment_segment_id)

        const defaultMainTrack = this.createDefaultModeTrackMainIfNotExist()
        const defaultMainTrackIndex = this.tracks.findIndex(
          (t) => t.id === defaultMainTrack.id,
        )

        this.tracks.splice(defaultMainTrackIndex + 1, 0, mainTack)
      }

      return mainTack
    },

    // 判断一个元件是否正在裁剪
    isCutting(segment: SegmentCollection): boolean {
      return this.cutting && segment.id === this.activeSegment?.id
    },

    /**
     * 根据指定轨道，获取指定的剧本
     * @param track
     * @returns
     */
    getCurrentTracksByTrack(track: Track): Track[] {
      if (this.templateTracks.some((t) => t.id === track.id)) {
        return this.templateTracks
      } else if (this.defaultTracks.some((t) => t.id === track.id)) {
        if (isFragmentTrack(track)) {
          return this.defaultTracks.filter(
            (t) => t.fragment_segment_id === track.fragment_segment_id,
          )
        }

        return this.defaultExcludeFragmentTracks
      } else if (this.subtitleTracks.some((t) => t.id === track.id)) {
        return this.subtitleTracks
      }
      return []
    },

    /**
     * 根据指定轨道id，获取指定的剧本
     * @param track
     * @returns
     */
    getCurrentTracksByTrackId(trackId: Track['id']): Track[] {
      const track =
        this.tracks.find((t) => t.id === trackId) ??
        this.defaultTracks.find((t) => t.id === trackId)

      if (track) {
        return this.getCurrentTracksByTrack(track)
      }

      return []
    },

    /**
     * 默认剧本不包括片段剧本
     * @deprecated
     * @param mode
     * @returns
     */
    getCurrentTracksByMode(mode: ProjectMode): Track[] {
      return mode === ProjectMode.Default
        ? this.defaultExcludeFragmentTracks
        : this.subtitleTracks
    },

    getTracksByFragmentSegmentId(
      fragment_segment_id: SegmentCollection['fragment_segment_id'],
    ): Track[] {
      return getTracksByFragmentSegmentId(fragment_segment_id, this.tracks)
    },

    /**
     * 获取当前这在操作的剧本轨道
     * @param mode
     * @returns
     */
    getCurrentTracks(): Track[] {
      const projectStore = useProjectStore()

      if (
        projectStore.tab === ProjectTab.MultiRatio &&
        this.templateTracks.length > 0
      ) {
        return this.templateTracks
      } else if (
        projectStore.tab === ProjectTab.Creative ||
        this.templateTracks.length === 0
      ) {
        if (projectStore.mode === ProjectMode.Subtitle) {
          return this.subtitleTracks
        } else if (projectStore.mode === ProjectMode.Default) {
          if (this.isEnterSegmentFragmentEditMode) {
            return this.currentEditingSegmentFragmentTracks ?? []
          }

          return this.defaultExcludeFragmentTracks
        }
      }

      return []
    },
  },
})
